import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { fetchBalance, fetchPortfolio, fetchRecentTrades, fetchAIRecommendations } from '../../../services/stockai/api/client';

function formatKrw(n) {
  return `${Math.round(n || 0).toLocaleString()}원`;
}

function formatSignedKrw(n) {
  const value = Math.round(n || 0);
  return `${value > 0 ? '+' : ''}${value.toLocaleString()}원`;
}

export default function Dashboard() {
  const { loggedIn, mock, openLogin, authTick } = useOutletContext() || {};
  const [balance, setBalance] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [trades, setTrades] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authRequired, setAuthRequired] = useState(false);

  useEffect(() => {
    if (!mock && !loggedIn) {
      setLoading(false);
      setAuthRequired(true);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setAuthRequired(false);
    (async () => {
      try {
        const [b, p, t, r] = await Promise.all([
          fetchBalance(),
          fetchPortfolio(),
          fetchRecentTrades(),
          fetchAIRecommendations(),
        ]);
        if (cancelled) return;
        const normalizedBalance = b?.balance || b;
        const holdings = p?.portfolio || [];
        const totalPl = holdings.reduce((sum, h) => sum + (h.profit_loss || 0), 0);
        if (!normalizedBalance?.profit_loss && totalPl) {
          normalizedBalance.profit_loss = totalPl;
        }
        setBalance(normalizedBalance);
        setPortfolio(holdings);
        setTrades(t?.trades || []);
        setRecommendations(r?.recommendations || []);
      } catch (err) {
        if (!cancelled && (err.status === 401 || err.message === 'Authentication required')) {
          setAuthRequired(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loggedIn, mock, authTick]);

  if (loading) return <p className="stockai-card">로딩 중...</p>;

  const pl = balance?.profit_loss ?? 0;
  const plClass = pl >= 0 ? 'stockai-positive' : 'stockai-negative';
  const showLoginHint = !mock && (authRequired || !loggedIn);
  const primaryRecommendation = recommendations[0];
  const topHolding = [...portfolio].sort((a, b) => (b.total_value || 0) - (a.total_value || 0))[0];

  return (
    <div>
      {showLoginHint && (
        <div className="stockai-alert">
          실 API 연동 — StockAI에서 바로 로그인하세요.{' '}
          <button type="button" className="stockai-btn stockai-btn--sm" onClick={openLogin}>로그인</button>
        </div>
      )}
      <div className="stockai-kicker">AI Trading Studio</div>
      <section className="stockai-hero">
        <div className="stockai-hero__panel">
          <h1 className="stockai-hero__headline">AI가 읽고, 사람이 승인하는 매매 대시보드</h1>
          <p className="stockai-hero__lead">
            추천 종목, 포트폴리오 리스크, 최근 체결 이력을 한 화면에서 확인하고
            즉시 매수·매도까지 이어지는 제우스 StockAI 목업 레이아웃입니다.
          </p>
          <div className="stockai-hero__actions">
            <button type="button" className="stockai-btn" onClick={openLogin}>
              {loggedIn || mock ? '실행 준비 완료' : '실 API 로그인'}
            </button>
            <span className="stockai-badge">
              {mock ? 'Mock Data Flow' : loggedIn ? 'Trading Connected' : 'Login Required'}
            </span>
          </div>
        </div>
        <div className="stockai-hero__aside">
          <div className="stockai-card stockai-stat-card">
            <div className="stockai-metric">
              <span className="stockai-metric__eyebrow">총 자산</span>
              <strong className="stockai-metric__value">{formatKrw(balance?.total_assets)}</strong>
              <span className="stockai-metric__meta">예수금 {formatKrw(balance?.cash)} / 주식 {formatKrw(balance?.stock_value)}</span>
            </div>
          </div>
          <div className="stockai-card stockai-stat-card">
            <div className="stockai-metric">
              <span className="stockai-metric__eyebrow">오늘의 손익</span>
              <strong className={`stockai-metric__value ${plClass}`}>{formatSignedKrw(pl)}</strong>
              <span className="stockai-metric__meta">수익률 {(balance?.profit_loss_rate ?? 0).toFixed(2)}%</span>
            </div>
          </div>
        </div>
      </section>

      <div className="stockai-grid stockai-grid--3">
        <div className="stockai-card stockai-stat-card">
          <div className="stockai-stat-label">보유 종목 수</div>
          <div className="stockai-stat-value">{portfolio.length}개</div>
          <div className="stockai-stat-label">실시간 자산 스냅샷</div>
        </div>
        <div className="stockai-card stockai-stat-card">
          <div className="stockai-stat-label">AI 추천 수</div>
          <div className="stockai-stat-value">{recommendations.length}건</div>
          <div className="stockai-stat-label">추천 강도 상위 종목 표시</div>
        </div>
        <div className="stockai-card stockai-stat-card">
          <div className="stockai-stat-label">최근 거래</div>
          <div className="stockai-stat-value">{trades.length}건</div>
          <div className="stockai-stat-label">7일 체결 이력 기준</div>
        </div>
      </div>

      <section className="stockai-section">
        <div className="stockai-section__header">
          <div>
            <h2 className="stockai-section__title">AI 추천 & 포트폴리오 인사이트</h2>
            <div className="stockai-section__subtitle">목업 카드 구성을 React 데이터와 연결했습니다.</div>
          </div>
        </div>
        <div className="stockai-panel-grid">
          <div className="stockai-card">
            <div className="stockai-recommendation__top">
              <h3 className="stockai-card__title">추천 시그널</h3>
              {primaryRecommendation && <span className="stockai-badge">{primaryRecommendation.confidence}% confidence</span>}
            </div>
            {primaryRecommendation ? (
              <>
                <h2 className="stockai-page-title" style={{ marginBottom: '0.6rem' }}>{primaryRecommendation.name}</h2>
                <p className="stockai-stat-label" style={{ marginBottom: '1rem' }}>{primaryRecommendation.reason || 'AI 모델이 거래량과 수급 흐름을 바탕으로 추출한 추천입니다.'}</p>
                <div className="stockai-chip-list">
                  {recommendations.slice(0, 4).map((rec) => (
                    <span key={rec.symbol} className="stockai-chip">
                      <strong>{rec.symbol}</strong> {rec.action === 'buy' ? '매수' : rec.action}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <p className="stockai-stat-label">추천 종목이 없습니다.</p>
            )}
          </div>
          <div className="stockai-card">
            <h3 className="stockai-card__title">핵심 포트폴리오</h3>
            {topHolding ? (
              <>
                <div className="stockai-metric">
                  <span className="stockai-metric__eyebrow">{topHolding.symbol}</span>
                  <strong className="stockai-metric__value">{topHolding.name}</strong>
                  <span className="stockai-metric__meta">
                    {topHolding.quantity}주 · 평가 {formatKrw(topHolding.total_value)}
                  </span>
                </div>
                <div className={`stockai-stat-value ${topHolding.profit_loss >= 0 ? 'stockai-positive' : 'stockai-negative'}`} style={{ marginTop: '1rem' }}>
                  {formatSignedKrw(topHolding.profit_loss)}
                </div>
              </>
            ) : (
              <p className="stockai-stat-label">보유 종목이 없습니다.</p>
            )}
          </div>
        </div>
      </section>

      <section className="stockai-section">
        <div className="stockai-section__header">
          <div>
            <h2 className="stockai-section__title">최근 거래</h2>
            <div className="stockai-section__subtitle">체결 로그와 추천 로그를 같은 톤으로 정리했습니다.</div>
          </div>
        </div>
        <div className="stockai-grid stockai-grid--2">
          <div className="stockai-card">
            <h3 className="stockai-card__title">체결 히스토리</h3>
            <ul className="stockai-mini-list">
              {trades.slice(0, 5).map((t) => (
                <li key={t.id || `${t.symbol}-${t.price}`}>
                  <div className="stockai-trade__top">
                    <strong>{t.stock_name || t.symbol}</strong>
                    <span className={t.order_type === 'buy' ? 'stockai-positive' : 'stockai-negative'}>
                      {t.order_type}
                    </span>
                  </div>
                  <div className="stockai-stat-label">{t.quantity}주 / {formatKrw(t.price)}</div>
                </li>
              ))}
            </ul>
          </div>
          <div className="stockai-card">
            <h3 className="stockai-card__title">운영 상태</h3>
            <div className="stockai-chip-list">
              <span className="stockai-chip">실행 모드 {mock ? 'Mock' : 'Live'}</span>
              <span className="stockai-chip">연결 상태 {loggedIn || mock ? 'Ready' : 'Pending'}</span>
              <span className="stockai-chip">추천 엔진 {recommendations.length ? 'Active' : 'Idle'}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="stockai-section">
        <div className="stockai-section__header">
          <div>
            <h2 className="stockai-section__title">보유 종목</h2>
            <div className="stockai-section__subtitle">포트폴리오 {portfolio.length}개 종목</div>
          </div>
        </div>
      </section>

      <div className="stockai-card">
        <table className="stockai-table">
          <thead>
            <tr><th>종목</th><th>수량</th><th>현재가</th><th>손익</th></tr>
          </thead>
          <tbody>
            {portfolio.map((h) => (
              <tr key={h.symbol}>
                <td>{h.name || h.symbol}</td>
                <td>{h.quantity}</td>
                <td>{formatKrw(h.currentPrice)}</td>
                <td className={h.profit_loss >= 0 ? 'stockai-positive' : 'stockai-negative'}>
                  {formatKrw(h.profit_loss)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
